import type { ChangeEvent, JSX, SubmitEvent } from 'react'

import { AuthFormBoxInternalTemplate } from "/app/libraries/arcana/src/blocks/AuthFormBox/Templates/index.ts"
import { AuthFormBoxRowGaps } from "/app/libraries/arcana/src/blocks/AuthFormBox/index.ts"
import { PhoneNumberInputBlock } from "/app/libraries/arcana/src/blocks/InputBlock/Templates/index.ts"
import { PrimarySubmitButton } from "/app/libraries/arcana/src/atoms/Button/Templates/index.ts"

import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import { useCallback } from 'react'
import { $viewError } from '../../model'
import { RememberMe } from '../../ui/RememberMe'
import { useSubmitLocked } from '../../ui/useSubmitLocked'
import { signInByPhoneForm, signInByPhoneFx } from '../model'
import { $signInFormTranslations } from '../translations'

export const SignInByPhoneForm = (): JSX.Element => {
  const formTranslations = useUnit($signInFormTranslations)

  const isSubmitting = useSubmitLocked(signInByPhoneFx.pending, $viewError)
  const { submit, eachValid, fields, errorText, hasError } =
    useForm(signInByPhoneForm)

  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    submit()
  }

  const getErrorMessage = (type: Parameters<typeof errorText>[0]): string => {
    const text = errorText(type, formTranslations[type].errors)
    return hasError(type) ? text : ''
  }

  const validatePhone = (phone: string): boolean => /^\+?\d*$/.test(phone)
  const handleChangePhone = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      validatePhone(e.target.value) && // do not allow to type anything except numbers
      fields.phoneNumber.onChange(e.target.value),
    [fields.phoneNumber]
  )

  return (
    <form onSubmit={onSubmit} autoComplete='off' noValidate>
      <AuthFormBoxInternalTemplate rowGap={AuthFormBoxRowGaps.SIZE_200}>
        <PhoneNumberInputBlock
          label={formTranslations.phoneNumber.label}
          value={fields.phoneNumber.value}
          onChange={handleChangePhone}
          onBlur={() => fields.phoneNumber.onBlur()}
          disabled={isSubmitting}
          errorMessage={getErrorMessage('phoneNumber')}
          autoComplete='one-time-code'
        />
        <RememberMe disabled={isSubmitting} />
        <PrimarySubmitButton
          title={
            isSubmitting
              ? formTranslations.submittingButton
              : formTranslations.submitButton
          }
          disabled={isSubmitting || !eachValid}
          withLoader
          isLoading={isSubmitting}
          dataTestId='signin-btn'
        />
      </AuthFormBoxInternalTemplate>
    </form>
  )
}
